import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, FileImage, FileCode2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LOGO_KIT } from "@/data/content";
import { EASE } from "@/components/Reveal";

const SWATCHES = [
  { name: "Indigo", hex: "#2A3192" },
  { name: "Cyan", hex: "#4FC3E8" },
  { name: "Cornflower", hex: "#5271FF" },
  { name: "Navy", hex: "#070B24" },
  { name: "Ice", hex: "#F0F9FF" },
];

const Asset = ({ a, i }) => (
  <motion.article
    data-testid={`brand-asset-${a.file}`}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay: 0.05 * i, ease: EASE }}
    className="overflow-hidden rounded-[1.5rem] border border-brand-indigo/10 bg-white"
  >
    <div className={`flex h-48 items-center justify-center overflow-hidden p-8 ${a.bg === "dark" ? "bg-brand-navy" : "bg-brand-ice"}`}>
      <img src={`/brand/${a.file}.png`} alt={a.title} className="max-h-32 w-auto max-w-full object-contain" />
    </div>
    <div className="p-6">
      <h3 className="font-display text-lg font-semibold text-brand-ink">{a.title}</h3>
      <p className="mt-1 text-sm text-slate-500">{a.note}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {!a.noSvg && <a data-testid={`download-svg-${a.file}`} href={`/brand/${a.file}.svg`} download className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/20 px-4 py-2 text-xs font-semibold text-brand-indigo transition hover:bg-brand-ice"><FileCode2 className="h-3.5 w-3.5" />SVG</a>}
        <a data-testid={`download-png-${a.file}`} href={`/brand/${a.file}.png`} download className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/20 px-4 py-2 text-xs font-semibold text-brand-indigo transition hover:bg-brand-ice"><FileImage className="h-3.5 w-3.5" />PNG</a>
        {a.signature && (
          <a data-testid={`download-signature-${a.file}`} href={`/brand/${a.file}-signature.png`} download className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/20 px-4 py-2 text-xs font-semibold text-brand-indigo transition hover:bg-brand-ice"><FileImage className="h-3.5 w-3.5" />PNG 600px</a>
        )}
      </div>
    </div>
  </motion.article>
);

export default function BrandKit() {
  return (
    <div data-testid="brand-kit-page" className="min-h-screen bg-brand-ice">
      <header className="border-b border-brand-indigo/10 bg-white">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <Link to="/" data-testid="brand-home-link"><Logo testId="brand-logo" /></Link>
          <Link to="/" data-testid="brand-back-link" className="inline-flex items-center gap-2 text-sm text-brand-indigo hover:underline"><ArrowLeft className="h-4 w-4" />Back to website</Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-indigo">Brand assets</div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-brand-ink sm:text-4xl lg:text-5xl">Malansh logo kit</h1>
            <p className="mt-4 max-w-xl text-sm text-slate-600 sm:text-base">Vector SVGs for print and decks, high-resolution PNGs for slides and documents, and 600px flattened versions for email signatures. Light and dark variants of every lockup.</p>
          </div>
          <a data-testid="download-logo-kit-zip" href="/brand/malansh-logo-kit.zip" download className="btn-primary"><span>Download full kit (.zip)</span><Download className="h-4 w-4" /></a>
        </div>

        <div className="mt-10 grid gap-3 sm:grid-cols-5">
          {SWATCHES.map((s) => (
            <div key={s.hex} data-testid={`swatch-${s.name.toLowerCase()}`} className="rounded-2xl border border-brand-indigo/10 bg-white p-3">
              <div className="h-14 rounded-xl border border-black/5" style={{ background: s.hex }} />
              <div className="mt-2 flex items-center justify-between text-xs"><span className="font-semibold text-brand-ink">{s.name}</span><span className="font-mono text-slate-500">{s.hex}</span></div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {LOGO_KIT.map((a, i) => <Asset key={a.file} a={a} i={i} />)}
        </div>

        <div className="mt-12 rounded-[1.5rem] border border-brand-indigo/10 bg-white p-6 text-sm text-slate-600 sm:p-8">
          <h2 className="font-display text-lg font-semibold text-brand-ink">Usage notes</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5">
            <li>Keep clear space around the mark equal to at least the height of the ring's stroke gap.</li>
            <li>Use the indigo version on white or Ice backgrounds; the white version on Navy, Indigo or photography.</li>
            <li>Don't recolour the cyan peak — it's the part of the mark that means "growth beyond the circle".</li>
            <li>Fonts: Outfit (wordmark) and JetBrains Mono (descriptor). Both are free on Google Fonts.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
